import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaymentRoundControllerService } from 'src/app/admin-api';
import { TableColumn } from 'src/app/components/table';
import { BillingCycleService } from 'src/app/services/billing-cycle.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  BillingMonthAttemptResult,
  BillingMonthGatewaySectionView,
  BillingMonthRefusalReason,
  BillingMonthReport,
  BillingMonthRoundOption,
  buildBillingMonthReport,
  buildGatewayApprovalSection,
  buildRefusalReasons,
  emptyGatewayApprovalSection
} from './billing-month.util';

type CardTone = 'neutral' | 'positive' | 'negative' | 'progress' | 'accent';

interface BillingMonthCard {
  label: string;
  value: string;
  hint: string;
  tone: CardTone;
}

interface ChartDataset {
  type: 'bar' | 'line';
  label: string;
  data: Array<number | null>;
  yAxisID?: string;
  stack?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  tension?: number;
  fill?: boolean;
  spanGaps?: boolean;
}

interface ChartData {
  labels: Array<string>;
  datasets: Array<ChartDataset>;
}

interface GatewayAttemptOption {
  label: string;
  value: number | null;
}

const COLOR_APPROVED = '#22c55e';
const COLOR_REFUSED = '#ef4444';
const COLOR_IN_PROGRESS = '#f59e0b';
const COLOR_CONVERSION = '#2563eb';

function emptyChart(): ChartData {
  return { labels: [], datasets: [] };
}

@Component({
  selector: 'app-billing-month',
  templateUrl: './billing-month.component.html',
  styleUrls: ['./billing-month.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BillingMonthComponent implements OnInit {
  private billingCycleService = inject(BillingCycleService);
  private paymentRoundService = inject(PaymentRoundControllerService);
  private title = inject(Title);

  monthForm = new FormGroup({
    month: new FormControl<Date>(new Date(), Validators.required)
  });

  refusalForm = new FormGroup({
    paymentRoundId: new FormControl<number>(null)
  });

  gatewayForm = new FormGroup({
    attempt: new FormControl<number | null>(0)
  });

  maxMonth: Date = new Date();
  selectedMonthLabel = '';

  report: BillingMonthReport | null = null;
  loadFailed = false;

  gatewaySection: BillingMonthGatewaySectionView = emptyGatewayApprovalSection();
  gatewayLoading = true;
  gatewayLoadFailed = false;
  gatewayAttemptOptions: Array<GatewayAttemptOption> =
    BillingMonthComponent.buildGatewayAttemptOptions(null, null);
  readonly gatewayAttemptDropdownStyle = { minWidth: '12rem' };
  private gatewayRequestSeq = 0;

  cards: Array<BillingMonthCard> = [];
  attempts: Array<BillingMonthAttemptResult> = [];
  roundOptions: Array<BillingMonthRoundOption> = [];
  refusalReasons: Array<BillingMonthRefusalReason> = [];
  refusalRoundLabel = '';

  timelineChartData: ChartData = emptyChart();
  attemptChartData: ChartData = emptyChart();
  refusalChartData: ChartData = emptyChart();

  readonly volumeChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    animation: { duration: 0 },
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'bottom' } },
    scales: {
      x: { grid: { display: false } },
      y: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        title: { display: true, text: 'Cobranças' }
      },
      y1: {
        type: 'linear',
        position: 'right',
        min: 0,
        max: 100,
        title: { display: true, text: 'Conversão (%)' },
        grid: { drawOnChartArea: false }
      }
    }
  };

  readonly stackedChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    animation: { duration: 0 },
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'bottom' } },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: {
        stacked: true,
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        title: { display: true, text: 'Cobranças' }
      },
      y1: {
        type: 'linear',
        position: 'right',
        min: 0,
        max: 100,
        title: { display: true, text: 'Conversão (%)' },
        grid: { drawOnChartArea: false }
      }
    }
  };

  readonly refusalChartOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    animation: { duration: 0 },
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, title: { display: true, text: 'Cobranças recusadas' } },
      y: { grid: { display: false } }
    }
  };

  attemptCols: Array<TableColumn> = [
    new TableColumn.Builder()
      .setHeader('Tentativa')
      .setField('attempt')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Cobranças')
      .setField('total')
      .setType('formattedInteger')
      .build(),
    new TableColumn.Builder()
      .setHeader('Aprovadas')
      .setField('approved')
      .setType('formattedInteger')
      .build(),
    new TableColumn.Builder()
      .setHeader('Recusadas')
      .setField('refused')
      .setType('formattedInteger')
      .build(),
    new TableColumn.Builder()
      .setHeader('Em processamento')
      .setField('inProgress')
      .setType('formattedInteger')
      .build(),
    new TableColumn.Builder()
      .setHeader('Conversão')
      .setField('conversionPercent')
      .setDisplayFunction((row: BillingMonthAttemptResult) =>
        BillingMonthComponent.formatPercent(row.conversionPercent)
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Margem vs 1ª')
      .setField('marginVsFirstAttempt')
      .setDisplayFunction((row: BillingMonthAttemptResult) =>
        BillingMonthComponent.formatMargin(row.marginVsFirstAttempt)
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Lotes')
      .setField('rounds')
      .setType('number')
      .build()
  ];

  gatewayCols: Array<TableColumn> = [
    new TableColumn.Builder().setHeader('Gateway').setField('gatewayName').build(),
    new TableColumn.Builder()
      .setHeader('Assinantes')
      .setField('distinctSubscribers')
      .build(),
    new TableColumn.Builder().setHeader('Pagos').setField('paid').build(),
    new TableColumn.Builder().setHeader('Recusados').setField('refused').build(),
    new TableColumn.Builder().setHeader('Em voo').setField('inFlight').build(),
    new TableColumn.Builder()
      .setHeader('Taxa de aprovação')
      .setField('approvalRatePercent')
      .build(),
    new TableColumn.Builder()
      .setHeader('Participação')
      .setField('sharePercent')
      .build()
  ];

  refusalCols: Array<TableColumn> = [
    new TableColumn.Builder().setHeader('Motivo').setField('reason').build(),
    new TableColumn.Builder()
      .setHeader('Etapa')
      .setField('steps')
      .setDisplayFunction((row: BillingMonthRefusalReason) => row.steps ?? '—')
      .build(),
    new TableColumn.Builder()
      .setHeader('Cobranças')
      .setField('total')
      .setType('formattedInteger')
      .build(),
    new TableColumn.Builder()
      .setHeader('% das recusas do lote')
      .setField('sharePercent')
      .setDisplayFunction((row: BillingMonthRefusalReason) =>
        BillingMonthComponent.formatPercent(row.sharePercent)
      )
      .build()
  ];

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Acompanhamento do Mês');
    await this.loadMonth();
  }

  async goToPreviousMonth(): Promise<void> {
    this.shiftMonth(-1);
    await this.loadMonth();
  }

  async goToNextMonth(): Promise<void> {
    this.shiftMonth(1);
    await this.loadMonth();
  }

  async loadMonth(): Promise<void> {
    const selectedMonth = this.monthForm.controls.month.value;
    if (!selectedMonth) {
      this.gatewayLoading = false;
      return;
    }

    const requestSeq = ++this.gatewayRequestSeq;
    const yyyymm = BillingMonthComponent.formatYyyymm(selectedMonth);
    const attempt = this.selectedGatewayAttempt();
    this.gatewayLoading = true;
    this.gatewayLoadFailed = false;

    LoaderService.showLoader();
    try {
      const [roundsResult, gatewayResult] = await Promise.allSettled([
        lastValueFrom(this.billingCycleService.listLotes(yyyymm)),
        lastValueFrom(this.billingCycleService.getGatewayReport(yyyymm, attempt))
      ]);

      if (roundsResult.status === 'fulfilled') {
        this.report = buildBillingMonthReport(roundsResult.value);
        this.loadFailed = false;
        this.applyReport(selectedMonth);
      } else {
        this.clearReport();
        this.loadFailed = true;
        AppDialogService.showErrorDialog(roundsResult.reason);
      }

      if (requestSeq !== this.gatewayRequestSeq) {
        return;
      }

      if (gatewayResult.status === 'fulfilled') {
        this.gatewaySection = buildGatewayApprovalSection(gatewayResult.value);
        this.gatewayLoadFailed = false;
      } else {
        this.gatewaySection = emptyGatewayApprovalSection();
        this.gatewayLoadFailed = true;
        AppDialogService.showErrorDialog(gatewayResult.reason);
      }
    } finally {
      if (requestSeq === this.gatewayRequestSeq) {
        this.gatewayLoading = false;
      }
      LoaderService.showLoader(false);
    }
  }

  async loadGatewayReport(): Promise<void> {
    const selectedMonth = this.monthForm.controls.month.value;
    if (!selectedMonth) {
      return;
    }

    const requestSeq = ++this.gatewayRequestSeq;
    this.gatewayLoading = true;
    this.gatewayLoadFailed = false;
    LoaderService.showLoader();
    try {
      const report = await lastValueFrom(
        this.billingCycleService.getGatewayReport(
          BillingMonthComponent.formatYyyymm(selectedMonth),
          this.selectedGatewayAttempt()
        )
      );
      if (requestSeq !== this.gatewayRequestSeq) {
        return;
      }
      this.gatewaySection = buildGatewayApprovalSection(report);
    } catch (error) {
      if (requestSeq !== this.gatewayRequestSeq) {
        return;
      }
      this.gatewaySection = emptyGatewayApprovalSection();
      this.gatewayLoadFailed = true;
      AppDialogService.showErrorDialog(error);
    } finally {
      if (requestSeq === this.gatewayRequestSeq) {
        this.gatewayLoading = false;
      }
      LoaderService.showLoader(false);
    }
  }

  async loadRefusalReasons(): Promise<void> {
    const paymentRoundId = this.refusalForm.controls.paymentRoundId.value;
    if (!paymentRoundId) {
      this.refusalReasons = [];
      this.refusalChartData = emptyChart();
      this.refusalRoundLabel = '';
      return;
    }

    LoaderService.showLoader();
    try {
      const summaries = await lastValueFrom(
        this.paymentRoundService
          .getPaymentRoundErrorsSummaryById(paymentRoundId)
          .pipe(map((data) => data.result ?? []))
      );
      this.refusalReasons = buildRefusalReasons(summaries);
      this.refusalRoundLabel =
        this.roundOptions.find(
          (option) => option.paymentRoundId === paymentRoundId
        )?.label ?? `Lote ${paymentRoundId}`;
      this.rebuildRefusalChart();
    } catch (error) {
      this.refusalReasons = [];
      this.refusalChartData = emptyChart();
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private applyReport(selectedMonth: Date): void {
    this.selectedMonthLabel = new Intl.DateTimeFormat('pt-BR', {
      month: 'long',
      year: 'numeric'
    }).format(selectedMonth);

    this.attempts = this.report.attempts;
    this.roundOptions = this.report.roundsWithRefusal;
    this.syncGatewayAttemptOptions();
    this.cards = this.buildCards();
    this.rebuildTimelineChart();
    this.rebuildAttemptChart();
    this.resetRefusalSelection();
  }

  private clearReport(): void {
    this.report = null;
    this.attempts = [];
    this.roundOptions = [];
    this.cards = [];
    this.timelineChartData = emptyChart();
    this.attemptChartData = emptyChart();
    this.syncGatewayAttemptOptions();
    this.resetRefusalSelection();
  }

  private syncGatewayAttemptOptions(): void {
    this.gatewayAttemptOptions = BillingMonthComponent.buildGatewayAttemptOptions(
      this.report?.attempts.map((row) => row.attempt) ?? null,
      this.gatewayForm.controls.attempt.value
    );
  }

  private selectedGatewayAttempt(): number | null {
    const attempt = this.gatewayForm.controls.attempt.value;
    if (attempt == null || !Number.isFinite(attempt) || attempt < 1) {
      return null;
    }
    return Math.trunc(attempt);
  }

  private static buildGatewayAttemptOptions(
    attempts: Array<number> | null,
    selected: number | null
  ): Array<GatewayAttemptOption> {
    const values = new Set<number>([1, 2, 3]);
    for (const attempt of attempts ?? []) {
      if (Number.isFinite(attempt) && attempt >= 1) {
        values.add(Math.trunc(attempt));
      }
    }
    if (selected != null && Number.isFinite(selected) && selected >= 1) {
      values.add(Math.trunc(selected));
    }
    return [
      { label: 'Todas', value: 0 },
      ...Array.from(values)
        .sort((left, right) => left - right)
        .map((attempt) => ({ label: String(attempt), value: attempt }))
    ];
  }

  private resetRefusalSelection(): void {
    this.refusalForm.controls.paymentRoundId.setValue(null);
    this.refusalReasons = [];
    this.refusalChartData = emptyChart();
    this.refusalRoundLabel = '';
  }

  private buildCards(): Array<BillingMonthCard> {
    const totals = this.report.totals;
    const lastAttempt =
      this.attempts.length > 1 ? this.attempts[this.attempts.length - 1] : null;

    return [
      {
        label: 'Cobranças no mês',
        value: BillingMonthComponent.formatNumber(totals.total),
        hint: 'Um registro por assinante em cada tentativa executada.',
        tone: 'neutral'
      },
      {
        label: 'Taxa de conversão',
        value: BillingMonthComponent.formatPercent(totals.conversionPercent),
        hint: 'Aprovadas sobre aprovadas + recusadas. Em processamento fica fora da conta.',
        tone: 'accent'
      },
      {
        label: 'Aprovadas',
        value: BillingMonthComponent.formatNumber(totals.approved),
        hint: 'Pagas e etapas seguintes do ciclo.',
        tone: 'positive'
      },
      {
        label: 'Recusadas',
        value: BillingMonthComponent.formatNumber(totals.refused),
        hint: 'Cobranças que terminaram em falha.',
        tone: 'negative'
      },
      {
        label: 'Em processamento',
        value: BillingMonthComponent.formatNumber(totals.inProgress),
        hint: 'Ainda em validação ou cobrança, sem desfecho.',
        tone: 'progress'
      },
      {
        label: lastAttempt
          ? `Margem da ${lastAttempt.attempt}ª tentativa vs 1ª`
          : 'Margem vs 1ª tentativa',
        value: BillingMonthComponent.formatMargin(
          lastAttempt?.marginVsFirstAttempt ?? null
        ),
        hint: lastAttempt
          ? 'Diferença da conversão da última tentativa contra a primeira do mês.'
          : 'Aparece quando o mês tiver uma segunda tentativa para comparar.',
        tone: BillingMonthComponent.resolveMarginTone(
          lastAttempt?.marginVsFirstAttempt ?? null
        )
      }
    ];
  }

  private rebuildTimelineChart(): void {
    const timeline = this.report.timeline;
    this.timelineChartData = {
      labels: timeline.map((point) => point.label),
      datasets: [
        {
          type: 'line',
          label: 'Aprovadas (acumulado)',
          yAxisID: 'y',
          data: timeline.map((point) => point.approved),
          borderColor: COLOR_APPROVED,
          backgroundColor: COLOR_APPROVED,
          borderWidth: 2,
          tension: 0.3
        },
        {
          type: 'line',
          label: 'Recusadas (acumulado)',
          yAxisID: 'y',
          data: timeline.map((point) => point.refused),
          borderColor: COLOR_REFUSED,
          backgroundColor: COLOR_REFUSED,
          borderWidth: 2,
          tension: 0.3
        },
        {
          type: 'line',
          label: 'Em processamento (acumulado)',
          yAxisID: 'y',
          data: timeline.map((point) => point.inProgress),
          borderColor: COLOR_IN_PROGRESS,
          backgroundColor: COLOR_IN_PROGRESS,
          borderWidth: 2,
          tension: 0.3
        },
        {
          type: 'line',
          label: 'Conversão acumulada (%)',
          yAxisID: 'y1',
          data: timeline.map((point) => point.conversionPercent),
          borderColor: COLOR_CONVERSION,
          backgroundColor: COLOR_CONVERSION,
          borderWidth: 2,
          tension: 0.3,
          spanGaps: true
        }
      ]
    };
  }

  private rebuildAttemptChart(): void {
    this.attemptChartData = {
      labels: this.attempts.map((row) => `${row.attempt}ª tentativa`),
      datasets: [
        {
          type: 'bar',
          label: 'Aprovadas',
          yAxisID: 'y',
          stack: 'result',
          data: this.attempts.map((row) => row.approved),
          backgroundColor: COLOR_APPROVED,
          borderRadius: 4
        },
        {
          type: 'bar',
          label: 'Recusadas',
          yAxisID: 'y',
          stack: 'result',
          data: this.attempts.map((row) => row.refused),
          backgroundColor: COLOR_REFUSED,
          borderRadius: 4
        },
        {
          type: 'bar',
          label: 'Em processamento',
          yAxisID: 'y',
          stack: 'result',
          data: this.attempts.map((row) => row.inProgress),
          backgroundColor: COLOR_IN_PROGRESS,
          borderRadius: 4
        },
        {
          type: 'line',
          label: 'Conversão (%)',
          yAxisID: 'y1',
          data: this.attempts.map((row) => row.conversionPercent),
          borderColor: COLOR_CONVERSION,
          backgroundColor: COLOR_CONVERSION,
          borderWidth: 2,
          tension: 0.3,
          spanGaps: true
        }
      ]
    };
  }

  private rebuildRefusalChart(): void {
    this.refusalChartData = {
      labels: this.refusalReasons.map((reason) => reason.reason),
      datasets: [
        {
          type: 'bar',
          label: 'Cobranças recusadas',
          data: this.refusalReasons.map((reason) => reason.total),
          backgroundColor: COLOR_REFUSED,
          borderRadius: 4
        }
      ]
    };
  }

  private shiftMonth(offset: number): void {
    const currentMonth = this.monthForm.controls.month.value;
    if (!currentMonth) {
      return;
    }
    const target = new Date(currentMonth);
    target.setDate(1);
    target.setMonth(target.getMonth() + offset);
    if (target > this.maxMonth) {
      return;
    }
    this.monthForm.controls.month.setValue(target);
  }

  private static formatYyyymm(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}${month}`;
  }

  private static formatNumber(value: number): string {
    return new Intl.NumberFormat('pt-BR').format(value ?? 0);
  }

  private static formatPercent(value: number | null): string {
    if (value === null || value === undefined) {
      return '—';
    }
    return `${value.toFixed(1).replace('.', ',')}%`;
  }

  private static resolveMarginTone(value: number | null): CardTone {
    if (value === null || value === 0) {
      return 'neutral';
    }
    return value > 0 ? 'positive' : 'negative';
  }

  private static formatMargin(value: number | null): string {
    if (value === null || value === undefined) {
      return '—';
    }
    const signal = value > 0 ? '+' : '';
    return `${signal}${value.toFixed(1).replace('.', ',')} pp`;
  }
}
